import { Component, OnInit } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { GenericTableComponent, TableColumn, TableConfig } from '@components/shared/generic-table/generic-table.component';
import { ColumnType } from '@models/shared.models';
import { MeterService } from '@services/meter.service';
import { ConsentService } from '@services/consent.service';
import { NotificationService } from '@services/notification.service';
import { SharedConsent } from '@models/consent.model';
import { Meter, ConsentStatus } from '@models/meter.model';
import { Notification } from '@models/notification.model';
import { combineLatest, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { LogService } from '@services/log.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, AsyncPipe, MatCardModule, MatIconModule, MatButtonModule, GenericTableComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  // Notifications
  notifications$: Observable<Notification[]>;
  hasMeters = false;
  energySavingOptions = [
    {
      tag: 'Tariff comparison',
      title: 'Comparison and switching energy tariffs',
      description: 'Compare tariffs and switch energy supplier plans to find a better fit for your home or business.',
      link: 'https://www.thelabrador.co.uk/',
      domain: 'thelabrador.co.uk',
      icon: 'compare_arrows'
    },
    {
      tag: 'Cost optimisation',
      title: 'Optimise energy costs',
      description: 'Explore tools that help you manage usage patterns and reduce overall energy spend.',
      link: 'https://optimise.perse.io',
      domain: 'optimise.perse.io',
      icon: 'savings'
    },
    {
      tag: 'Flexibility rewards',
      title: 'Get paid to save energy',
      description: 'Find flexibility opportunities that reward you for shifting or reducing demand at key times.',
      link: 'https://flexibility.perse.io',
      domain: 'flexibility.perse.io',
      icon: 'bolt'
    }
  ];

  // Dashboard metrics - will be calculated from services
  metrics = [
    {
      title: 'Total Meters',
      value: 0,
      icon: 'speed',
      route: '/meter-details'
    },
    {
      title: 'Active Consents',
      value: 0,
      icon: 'check_circle',
      route: '/meter-details'
    },
    {
      title: 'Expired Consents',
      value: 0,
      icon: 'schedule',
      route: '/meter-details'
    },
    {
      title: 'Shared Consents',
      value: 0,
      icon: 'share',
      route: '/meter-details'
    }
  ];

  constructor(
    readonly router: Router,
    private meterService: MeterService,
    private consentService: ConsentService,
    private notificationService: NotificationService,
    private logService: LogService
  ) {
    // Initialize notifications
    this.notifications$ = this.notificationService.getNotifications();
  }

  ngOnInit(): void {
    // Log page view
    this.logService.logPageView('dashboard', {});

    // Get all meters and all consents at once
    combineLatest([
      this.meterService.getMeters(),
      this.consentService.getAllSharedConsents()
    ]).pipe(
      take(1) // Take the first emission since we just need the current state
    ).subscribe(([meters, allConsents]) => {
      this.hasMeters = meters.length > 0;
      // Update metrics with the combined consents
      this.updateMetrics(meters, allConsents);
      this.updateUpcomingExpiries(meters);
    });
  }

  private updateMetrics(meters: Meter[], sharedConsents: SharedConsent[]): void {
    const totalMeters = meters.length;
    const activeConsents = meters.filter(m => m.consentStatus === ConsentStatus.ACTIVE).length;
    const expiredConsents = meters.filter(m => m.consentStatus === ConsentStatus.EXPIRED).length;
    const totalSharedConsents = sharedConsents.filter(c => c.consentStatus === ConsentStatus.ACTIVE).length;

    this.metrics[0].value = totalMeters;
    this.metrics[1].value = activeConsents;
    this.metrics[2].value = expiredConsents;
    this.metrics[3].value = totalSharedConsents;
  }

  private updateUpcomingExpiries(meters: Meter[]): void {
    const currentDate = new Date();
    const upcomingExpiriesData: any[] = [];

    // Process meters for upcoming expiries
    meters.forEach(meter => {
      const expiryDate = new Date(meter.expiryDate);
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
      
      // Include meters expiring within 90 days or already expired
      if (daysUntilExpiry <= 90) {
        upcomingExpiriesData.push({
          property: `${meter.address}, ${meter.postCode}`,
          partner: 'Direct Meter Access', // Meters don't have specific partners
          consentType: `${meter.meterType} - ${meter.fuelType}`,
          expiryDate: meter.expiryDate.toISOString().split('T')[0],
          daysUntilExpiry: daysUntilExpiry,
          status: meter.consentStatus
        });
      }
    });

    // Process shared consents for upcoming expiries (if they had expiry dates)
    // For now, shared consents don't have expiry dates in the model, so we'll focus on meters

    // Sort by days until expiry (expired items first, then by ascending days)
    this.upcomingExpiries = upcomingExpiriesData.sort((a, b) => {
      if (a.daysUntilExpiry < 0 && b.daysUntilExpiry >= 0) return -1;
      if (a.daysUntilExpiry >= 0 && b.daysUntilExpiry < 0) return 1;
      return a.daysUntilExpiry - b.daysUntilExpiry;
    });
  }

  // Table configuration
  tableConfig: TableConfig = {
    enablePagination: true,
    enableSort: true,
    enableSearch: true,
    enableFilter: true,
    pageSize: 10,
    pageSizeOptions: [5, 10, 25, 50]
  };

  // Table columns configuration
  expiryColumns: TableColumn[] = [
    {
      key: 'property',
      header: 'Property',
      sortable: true,
      filterable: true
    },
    {
      key: 'partner',
      header: 'Sharee',
      sortable: true,
      filterable: true
    },
    {
      key: 'consentType',
      header: 'Consent Type',
      sortable: true,
      filterable: true
    },
    {
      key: 'expiryDate',
      header: 'Expiry Date',
      sortable: true,
      type: ColumnType.Date
    },
    {
      key: 'daysUntilExpiry',
      header: 'Days Until Expiry',
      sortable: true,
      type: ColumnType.Number
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      filterable: true
    }
  ];

  // Upcoming expiries - will be calculated from services
  upcomingExpiries: any[] = [];

  // Notification handlers
  onAcceptNotification(notification: Notification): void {
    // Mark notification as read and execute its action (similar to onNotificationClick)
    this.notificationService.markAsRead(notification.id);
    notification.action();
  }

  onRejectNotification(notification: Notification): void {
    // Remove the notification (dismiss it)
    this.notificationService.removeNotification(notification.id);
  }

  onAddMeter(): void {
    // Navigate to meter-details with addMeter query parameter
    this.router.navigate(['/meter-details'], { queryParams: { addMeter: true } });
  }
}
